import { observable, action, runInAction, makeAutoObservable } from "mobx";
import agent from "../api/agent";
import { RootStore } from "./rootStore";
import { toast } from "react-toastify";
import {
  IDocumentTrace,
  IDocumentList,
  IAcceptRejectDocument,
} from "../models/trackDocument";
import { showError } from "../util/errorTypeEnumsOptions";

export default class InvoiceStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  @observable xml: string | null;
  @observable pdf: string | null;
  @observable loadPDF = false;
  @observable xmlb64: string | null;
  @observable loadXML = false;
  @observable documentTrace: IDocumentTrace[] = [];
  @observable documentNotification: IDocumentTrace[] = [];
  @observable invoice: IDocumentList | null = null;
  @observable btGraphic = false;
  @observable acceptRejectDocument: IAcceptRejectDocument | null = null;
  @observable loadByEmail = false;

  @action clearInvoice = () => {
    this.xml = null;
    this.pdf = null;
    this.xmlb64 = null;
    this.loadPDF = false;
    this.loadXML = false;
    this.documentTrace = [];
    this.invoice = null;
    this.btGraphic = false;
  };

  @action getPDFById = async (id: string) => {
    try {
      const pdf = await agent.Invoice.getPDFById(id);
      runInAction(() => {
        this.loadPDF = true;
        this.pdf = pdf;
      });
    } catch (error) {
      runInAction(() => {
        this.loadPDF = true;
        this.pdf = null;
      });
    }
  };

  @action getUBLById = async (id: string) => {
    try {
      const xml = await agent.Invoice.getUBLById(id);
      runInAction(() => {
        this.xml = xml;
        this.loadXML = true;
      });
    } catch (error) {
      runInAction(() => {
        this.xml = null;
        this.loadXML = true;
      });
    }
  };

  @action getUBLB64ById = async (id: string) => {
    try {
      const xmlb64 = await agent.Invoice.getUBLB64ById(id);
      runInAction(() => {
        this.xmlb64 = xmlb64;
      });
      fetch(`data:application/xml;base64,${xmlb64}`).then((response) => {
        response.blob().then((blob) => {
          let url = window.URL.createObjectURL(blob);
          let a = document.createElement("a");
          a.href = url;
          a.download = `${this.invoice.erpDocumentId}.xml`;
          a.click();
        });
      });
    } catch (error) {
      showError(error);
    }
  };

  @action getAttachDocumentB64 = async () => {
    try {
      const attachDocumentB64 = await agent.Invoice.getAttachDocumentB64ByErpDocumentId(
        this.invoice.erpDocumentId
      );
      fetch(`data:application/xml;base64,${attachDocumentB64}`).then((response) => {
        response.blob().then((blob) => {
          let url = window.URL.createObjectURL(blob);
          let a = document.createElement("a");
          a.href = url;
          a.download = `${this.invoice.erpDocumentId}_AttachedDocument.xml`;
          a.click();
        });
      });
    } catch (error) {
      showError(error);
    }
  };

  @action acceptDocument = async (id: string) => {
    try {
      const accept = await agent.Invoice.acceptDocument(id);
      runInAction(() => {
        this.btGraphic = true;
        this.invoice = accept;
        toast.success(
          "Documento aceptado correctamente, el registro puede demorar unos minutos"
        );
      });
    } catch (error) {
      showError(error);
    }
  };

  @action rejectDocument = async (id: string, reason: string) => {
    try {
      const reject = await agent.Invoice.rejectDocument(id, reason);
      runInAction(() => {
        this.btGraphic = true;
        this.invoice = reject;
        toast.success(
          "Documento rechazado correctamente, el registro puede demorar unos minutos"
        );
        this.rootStore.modalStore.closeModal();
      });
    } catch (error) {
      showError(error);
    }
  };

  @action acceptDocumentByEmail = async (id: string, token: string) => {
    try {
      await agent.Guest.acceptDocumentByEmail(id, token);
      runInAction(() => {
        this.btGraphic = true;
        toast.success("Documento aceptado correctamente");
      });
    } catch (error) {
      showError(error);
    }
  };

  @action getDocumentInfo = async (id: string, token: string) => {
    try {
      const doc = await agent.Guest.getDocumentInfo(id, token);
      runInAction(() => {
        this.acceptRejectDocument = doc.data;
        this.loadByEmail = true;
      });
    } catch (error) {
      showError(error);
    }
  };

  @action rejectDocumentByEmail = async (
    id: string,
    reason: string,
    token: string
  ) => {
    try {
      await agent.Guest.rejectDocumentByEmail(id, reason, token);
      runInAction(() => {
        this.btGraphic = true;
        toast.success("Documento rechazado correctamente");
        this.rootStore.modalStore.closeModal();
      });
    } catch (error) {
      showError(error);
    }
  };

  @action getDocumentTrace = async (id: string) => {
    try {
      const trace = await agent.Invoice.getDocumentTrace(id);
      runInAction(() => {
        this.documentTrace = trace;
      });
    } catch (error) {
      showError(error);
    }
  };

  @action getDocumentNotifications = async (id: string) => {
    try {
      const notifications = await agent.Invoice.getDocumentNotifications(id);
      runInAction(() => {
        this.documentNotification = notifications;
      });
    } catch (error) {
      showError(error);
    }
  };

  @action getDocumentById = async (id: string) => {
    try {
      const document = await agent.Invoice.getDocumentById(id);
      runInAction(() => {
        this.invoice = document;
      });
    } catch (error) {
      showError(error);
    }
  };
}
